import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { ProfileLayout } from '@/components/common/ProfileLayout';
import { ProfileLogo } from '@/components/common/ProfileLogo';
import { ProfileFact, ProfileFacts } from '@/components/common/ProfileFacts';
import { ProfileCardGrid, ProfileGridItem } from '@/components/common/ProfileCardGrid';
import { SectionEditButton } from '@/components/common/SectionEditButton';
import { ROLES, getPrimaryRole } from '@/lib/constants/roles';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { userInitials } from '@/lib/utils/user.utils';
import type { IndividualProfile } from '@/lib/types/profile.types';
import { EmailCard } from './EmailCard';
import { EmailPreferencesCard } from './EmailPreferencesCard';
import { DangerZoneCard } from './DangerZoneCard';
import { PasswordCard } from './PasswordCard';
import { TwoFactorCard } from './TwoFactorCard';
import { PersonalDetailsDrawer } from './PersonalDetailsDrawer';
import { formatPhoneDisplay } from '@/lib/utils/phone.utils';

const P = UI_STRINGS.PROFILE;

function fullNameOf(profile: IndividualProfile): string {
  return `${profile.firstName} ${profile.lastName}`.trim();
}

interface IdentityCardProps {
  profile: IndividualProfile;
  initials: string;
  onEdit: () => void;
}

/**
 * The person's identity card (mockup: 8 of 12 columns): photo, name, Edit,
 * then Phone and Country side by side. Edit opens Personal details.
 */
function IdentityCard({ profile, initials, onEdit }: IdentityCardProps) {
  const name = fullNameOf(profile);
  return (
    <section className="rounded-card shadow-card flex h-full flex-wrap items-center gap-7 bg-white p-8">
      <ProfileLogo url={profile.avatarUrl} name={name || initials} size={120} shape="circle" />
      <div className="min-w-0 flex-[1_1_20rem]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-navy font-sans text-[1.875rem] leading-[2.375rem] font-semibold">
            {name}
          </h1>
          <SectionEditButton section={P.SECTION_PERSONAL} onClick={onEdit} />
        </div>
        <div className="mt-[1.125rem]">
          <ProfileFacts columns={2}>
            <ProfileFact label={P.FIELD_PHONE} value={formatPhoneDisplay(profile.phone)} />
            {/* The resolved country NAME off the read DTO; the form posts `countryId` back. */}
            <ProfileFact label={P.FIELD_COUNTRY} value={profile.country} />
          </ProfileFacts>
        </div>
      </div>
    </section>
  );
}

/**
 * The individual person's profile at `/me` (FR-09), laid out as the
 * client-approved Option D: identity beside the two-step verification box,
 * Email beside Password, then Email preferences and the Danger Zone (MYN-217, FR-178/FR-179). The
 * person has no entity header, so this page keeps Option D's own identity
 * card. Personal details are edited in a side panel; the old `/me/edit` page
 * is gone. This is the person, not an entity (ADR FE-1).
 */
export function IndividualAccount() {
  const { user } = useAuth();
  const { data: profile, isPending, isError, refetch } = useProfile();
  const [isEditOpen, setIsEditOpen] = useState(false);
  // Email replacement (FR-14, MYN-76) is Individual-only and one-time;
  // `getPrimaryRole`, not raw `activeMembership`, per ADR FE-1.
  const isIndividual = user ? getPrimaryRole(user.roles) === ROLES.INDIVIDUAL : false;
  const initials = userInitials(profile, user?.email);

  // The Email preferences card (MYN-120) has its own query and stays usable
  // while GET /me is pending, failing or 404, so a member can always opt out.
  if (isPending || isError || !profile) {
    return (
      <ProfileLayout>
        {isPending ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" label="Loading profile" />
          </div>
        ) : (
          <ErrorState onRetry={() => void refetch()} />
        )}
        <EmailPreferencesCard />
      </ProfileLayout>
    );
  }

  return (
    <ProfileLayout>
      <ProfileCardGrid>
        <ProfileGridItem span={8}>
          <IdentityCard profile={profile} initials={initials} onEdit={() => setIsEditOpen(true)} />
        </ProfileGridItem>
        <ProfileGridItem span={4}>
          <TwoFactorCard />
        </ProfileGridItem>
        <ProfileGridItem span={7}>
          <EmailCard
            email={profile.email}
            canReplace={isIndividual && !profile.emailReplacementUsed}
          />
        </ProfileGridItem>
        <ProfileGridItem span={5}>
          <PasswordCard />
        </ProfileGridItem>
        <ProfileGridItem span={12}>
          <EmailPreferencesCard />
        </ProfileGridItem>
        <ProfileGridItem span={12}>
          <DangerZoneCard />
        </ProfileGridItem>
      </ProfileCardGrid>
      <PersonalDetailsDrawer
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        profile={profile}
        initials={initials}
      />
    </ProfileLayout>
  );
}
